import { throttle } from 'lodash';
import type {
    WboDelta,
    WboAnyAnnotationData,
    WboMsg,
    WboAnnotationDocumentData,
    WboAnnotationDocumentMetadata,
} from '../model/wbo-annotation.model';
import WboDocument from '../model/wbo-document';
import WboPendingChangesQueue from '../model/wbo-pending-changes-queue';
import GenericQueue from '../util/generic-queue';
import type WboPersistenceProvider from '../interfaces/wbo-persistence-provider.interface';

/**
 * Base implementation of WboPersistenceProvider that handles common persistence logic.
 * Subclasses must implement the abstract storage methods.
 */
export abstract class BaseWboPersistenceProvider implements WboPersistenceProvider {
    /**
     * The WboDocument to persist.
     */
    protected document: WboDocument;

    /**
     * The WboPendingOperationsQueue to persist.
     */
    protected pendingQueue: WboPendingChangesQueue;

    /**
     * The WBO board name on which this persistence provider will work.
     */
    protected readonly boardName: string;

    /**
     * Username of the board owner.
     */
    protected readonly username: string;

    /**
     * A function wrapper that throttles the save operation to avoid saving too many times in a short period.
     */
    protected readonly throttledSaveDoc: () => void;

    /**
     * Like the throttledSaveDoc, but for the pending operations queue.
     */
    protected readonly throttledSavePendingOps: () => void;

    constructor(boardName: string, username: string, throttleInterval = 1000) {
        this.document = new WboDocument();
        this.pendingQueue = new WboPendingChangesQueue(new GenericQueue<WboDelta>());
        this.boardName = boardName;
        this.username = username;

        this.throttledSaveDoc = throttle((): void => {
            this.saveDataToStorage(this.docKey, this.document.getData());
        }, throttleInterval);
        this.throttledSavePendingOps = throttle((): void => {
            this.saveDataToStorage(this.pendingOpsKey, this.pendingQueue.queue().elements());
        }, throttleInterval);
    }

    get docKey(): string {
        return `wbo_doc_${this.username}_${this.boardName}`;
    }

    get pendingOpsKey(): string {
        return `wbo_pending_${this.username}_${this.boardName}`;
    }

    /**
     * Type guard to check if the data is annotation data (not metadata)
     * @param data - The data to check
     * @param key - The key used to access the data
     * @returns True if the data is annotation data, false otherwise
     */
    private isAnnotationData(
        data: WboAnyAnnotationData | WboAnnotationDocumentMetadata | undefined,
        key: string
    ): data is WboAnyAnnotationData {
        return key !== 'metadata' && data !== undefined && typeof data === 'object' && 'type' in data;
    }

    /**
     * Gets the annotation data by its ID, first looking in the document, then in the pending ops if not found.
     * @param annotationId - The unique identifier of the annotation to retrieve
     * @returns The annotation data if found, undefined otherwise
     */
    getAnnotationDataById(annotationId: string): WboAnyAnnotationData | undefined {
        // Early return for invalid input
        if (!annotationId) {
            return undefined;
        }

        // First, look in the persisted document
        const docData = this.document.getData()[annotationId];
        if (this.isAnnotationData(docData, annotationId)) {
            return docData;
        }

        // If not found, look in the pending ops
        const pendingOps = this.pendingQueue.queue().elements();
        const foundInPending = pendingOps.find((delta) => {
            const msg = delta.msg as WboMsg;
            return msg?.data?.id === annotationId;
        });

        if (foundInPending?.msg.data) {
            return (foundInPending.msg as WboMsg).data as WboAnyAnnotationData;
        }

        // If not found, look in the inflight ops
        const inflightOpsQueue = this.pendingQueue.inflightOps().elements();
        const foundInInflight = inflightOpsQueue.find((delta) => {
            const msg = delta.msg as WboMsg;
            return msg?.data?.id === annotationId;
        });

        if (foundInInflight?.msg.data) {
            return (foundInInflight.msg as WboMsg).data as WboAnyAnnotationData;
        }

        return undefined;
    }

    async readAnnotationsFile(): Promise<WboDocument> {
        const wboDocument = new WboDocument();
        const annotations: WboAnnotationDocumentData = (await this.readDataFromStorage(
            this.docKey
        )) as WboAnnotationDocumentData;
        if (annotations) {
            wboDocument.setData(annotations);
        }
        this.document = wboDocument;
        return this.document;
    }

    async readPendingChangesFile(): Promise<WboPendingChangesQueue> {
        const pendingOperationsQueue = new GenericQueue<WboDelta>();
        const ops: WboDelta[] = (await this.readDataFromStorage(this.pendingOpsKey)) as WboDelta[];
        if (ops) {
            pendingOperationsQueue.setQueue(ops);
        }
        this.pendingQueue = new WboPendingChangesQueue(new GenericQueue<WboDelta>(ops));
        return this.pendingQueue;
    }

    async createAnnotationsFile(): Promise<void> {
        await this.saveAnnotationsFile();
    }

    async saveAnnotationsFile(): Promise<void> {
        this.throttledSaveDoc();
    }

    async deleteAnnotationsFile(): Promise<void> {
        await this.deleteFromStorage(this.docKey);
    }

    async createPendingChangesFile(): Promise<void> {
        await this.savePendingChangesFile();
    }

    async appendToChangesFile(delta: WboDelta): Promise<void> {
        this.pendingQueue.enqueue(delta);
        await this.savePendingChangesFile();
    }

    async savePendingChangesFile(): Promise<void> {
        this.throttledSavePendingOps();
    }

    async deletePendingChangesFile(): Promise<void> {
        await this.deleteFromStorage(this.pendingOpsKey);
    }

    /**
     * Saves data to the underlying storage implementation.
     * Subclasses must implement this method.
     * @param fileName - The storage key/filename
     * @param data - The data to save
     */
    abstract saveDataToStorage(fileName: string, data: WboAnnotationDocumentData | WboDelta[]): Promise<void>;

    /**
     * Reads data from the underlying storage implementation.
     * Subclasses must implement this method.
     * @param fileName - The storage key/filename
     * @returns The data read from storage
     */
    abstract readDataFromStorage(fileName: string): Promise<WboAnnotationDocumentData | WboDelta[]>;

    /**
     * Deletes data from the underlying storage implementation.
     * Subclasses must implement this method.
     * @param fileName - The storage key/filename
     */
    abstract deleteFromStorage(fileName: string): Promise<void>;
}
